import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {Panel, PanelHeader} from '@Components/panel';
import {BackgroundColorOption} from '@Panels/poster-editor-item-background-panel/components/background-color-option';
import {BackgroundColorOpacityOption} from '@Panels/poster-editor-item-background-panel/components/background-color-opacity-option';
import {BackgroundTypeOption} from '@Panels/poster-editor-item-background-panel/components/background-type-option';
import type {FillObject, FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {ContextualMenu} from '@Components/contextual-menu';
import type {RGBA} from '@Utils/color.util';
import PosterEditorColorPanel from '@Panels/poster-editor-color-panel';
import {useGetSelectedTranscriptItem} from '@Hooks/poster-editor/useGetSelectedTranscriptItem';
import type {TranscriptBackgroundPanelProps} from './transcript-background-panel.types';
import {getUniqueString} from '@Utils/string.util';
import {useGetKeyFromSubtitlePropertiesForTranscriptItem} from '@Hooks/poster-editor/useGetKeyFromSubtitlePropertiesForTranscriptItem';
import {useGetKeyFromSubtitleBackgroundFillPropertiesForActiveTranscriptItem} from '@Hooks/poster-editor/useGetKeyFromSubtitleBackgroundFillPropertiesForActiveTranscriptItem';

enum BackgroundPanels {
  DEFAULT = 'DEFAULT',
  COLOR_PICKER = 'COLOR_PICKER',
}

export function TranscriptBackgroundPanel({panelId = getUniqueString(), ...props}: TranscriptBackgroundPanelProps): ReactElement | null {
  const activeTranscriptItem = useGetSelectedTranscriptItem();
  const [panelToShow, setPanelToShow] = useState(BackgroundPanels.DEFAULT);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);

  const backgroundBorderRadius = useGetKeyFromSubtitlePropertiesForTranscriptItem('backgroundBorderRadius');
  const bgFillColor = useGetKeyFromSubtitleBackgroundFillPropertiesForActiveTranscriptItem('fillColor');
  const bgFillType = useGetKeyFromSubtitleBackgroundFillPropertiesForActiveTranscriptItem('fillType');

  if (!activeTranscriptItem) {
    return null;
  }

  if (backgroundBorderRadius === undefined) {
    throw new Error('Transcript item does not have backgroundBorderRadius in its subtitle objects hashmap');
  }

  if (bgFillColor === undefined) {
    throw new Error('Transcript item subtitle objects do not have a fillColor property in the backgroundFill object');
  }

  if (bgFillType === undefined) {
    throw new Error('Transcript item subtitle objects do not have a fillType property in the backgroundFill object');
  }

  const getHeader = (): ReactElement => {
    return (
      <PanelHeader
        onClose={(): void => {
          props.onClose();
        }}
        title={window.i18next.t('pmwjs_background')}
      />
    );
  };

  const updateOpacity = (fillAlpha: number, undoable = true): void => {
    const fillColor = activeTranscriptItem.getAnySubtitle().backgroundFill?.fillColor?.slice() as unknown as RGBA[];
    for (let i = 0; i < fillColor.length; i++) {
      const color = fillColor[i].slice();
      color[3] = fillAlpha;
      fillColor[i] = color as RGBA;
    }

    void activeTranscriptItem.updateAllSubtitlesStylesFromSubtitleObject(
      {
        backgroundFill: {
          fillColor,
        },
      },
      undoable
    );
  };

  const getFillObject = (): FillObject => {
    return {
      fillType: bgFillType,
      fillColor: bgFillColor,
    };
  };

  const getBackgroundOptions = (): ReactElement => {
    return (
      <>
        <BackgroundColorOption
          panelId={panelId}
          fillObject={getFillObject()}
          onClickColorFill={(index): void => {
            setSelectedColorIndex(index);
            setPanelToShow(BackgroundPanels.COLOR_PICKER);
          }}
        />
        <BackgroundColorOpacityOption fillObject={getFillObject()} updateOpacity={updateOpacity} />
        <BackgroundTypeOption updateBackgroundFillType={updateFillType} fillObject={getFillObject()} panelId={panelId} />
      </>
    );
  };

  const updateFillType = (newType: FillTypes): void => {
    void activeTranscriptItem.updateAllSubtitlesStylesFromSubtitleObject({
      backgroundFill: {
        fillType: newType,
        fillColor: activeTranscriptItem.getAnySubtitle().backgroundFill.getColorForNewType(newType),
      },
    });
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu id="text-background-mobile-panel" hasMobileMorphingProperties hasMobileModalPadding contentClassName="spacing-m-0">
        {getBackgroundOptions()}
      </ContextualMenu>
    );
  };

  const onChangeColor = (fillColor: RGBA[], undoable?: boolean): void => {
    void activeTranscriptItem.updateAllSubtitlesStylesFromSubtitleObject(
      {
        backgroundFill: {
          fillColor,
        },
      },
      undoable
    );
  };

  const getColorPanel = (): ReactElement => {
    return (
      <PosterEditorColorPanel
        panelId={panelId}
        fillObject={getFillObject()}
        onChangeColor={onChangeColor}
        onBackButton={(): void => {
          setPanelToShow(BackgroundPanels.DEFAULT);
        }}
        initialSelectedColorIndex={selectedColorIndex}
        onClose={(): void => {
          props.onClose();
        }}
      />
    );
  };

  const getPanel = (): ReactElement => {
    switch (panelToShow) {
      case BackgroundPanels.COLOR_PICKER:
        return getColorPanel();
      case BackgroundPanels.DEFAULT:
      default:
        return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
    }
  };

  return getPanel();
}
